import {
  takeLatest, put, call, all, select,
} from 'redux-saga/effects';
import history from '@rootCommon/utilities/history';
import { ROUTES } from '@common/constants/routes';
import UsersService from '@services/users';
import * as actions from '@actions/users';
import { RootState } from '@reducers';
import { IdType } from '@rootCommon/types/common';
import { Filters } from '@rootCommon/types/filter';
import Message from '@rootCommon/services/message';
import { User, UserDto, UserListItem } from '@rootCommon/types/user';

import { AxiosError } from 'axios';
import { SagaGenerator } from './index';

// fetch
function* fetchUsersListAsync() {
  const filters: Filters = yield select((state: RootState) => state.usersList.filter);

  try {
    const users: UserListItem[] = yield call(UsersService.getListByOrgId, filters);

    yield put(actions.fetchUsers.success({ items: users, total: users?.length }));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchUsers.failure(error));
  }
}

function* watchFetchUsersList() {
  yield takeLatest(actions.fetchUsers.request, fetchUsersListAsync);
}

// fetch item
function* fetchUserByIdAsync(action: { payload: IdType }) {
  try {
    const user: User = yield call(UsersService.getById, action.payload);

    yield put(actions.fetchUser.success(user));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchUser.failure(error));
  }
}

function* watchFetchUserById() {
  yield takeLatest(actions.fetchUser.request, fetchUserByIdAsync);
}

// create
function* createUserAsync(action: { payload: UserDto }) {
  try {
    const user: User = yield call(UsersService.create, action.payload);

    yield put(actions.createUser.success(user));
    Message.success(`${action.payload.firstName || 'User'} ${action.payload.lastName || ''} has been created`);
    history.push(ROUTES.USERS.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.createUser.failure(error));
  }
}

function* watchCreateUser() {
  yield takeLatest(actions.createUser.request, createUserAsync);
}

// update
function* updateUserAsync(action: { payload: { id: IdType; data: UserDto } }) {
  try {
    const user: User = yield call(UsersService.update, action.payload.id, action.payload.data);

    yield put(actions.updateUser.success(user));
    Message.success(
      `${action.payload.data.firstName || 'User'} ${action.payload.data.lastName || ''} has been updated`,
    );
    yield put(actions.setUserGroupId(null));
    yield put(actions.setUserPermissionCode(null));
    history.push(ROUTES.USERS.LIST);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.updateUser.failure(error));
  }
}

function* watchUpdateUser() {
  yield takeLatest(actions.updateUser.request, updateUserAsync);
}

// delete
function* deleteUsersAsync(action: { payload: IdType[] }) {
  try {
    yield call(UsersService.multipleDelete, { ids: action.payload });
    yield put(actions.deleteUsers.success(action.payload));
    history.push(ROUTES.USERS.LIST);
    Message.success(`${action.payload.length} User(s) has been deleted`);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.deleteUsers.failure(error));
  }
}

function* watchDeleteUsers() {
  yield takeLatest(actions.deleteUsers.request, deleteUsersAsync);
}

// enable
function* enableUsersAsync(action: { payload: IdType[] }) {
  try {
    yield call(UsersService.enable, { ids: action.payload });
    yield put(actions.enableUsers.success(action.payload));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.enableUsers.failure(error));
  }
}

function* watchEnableUsers() {
  yield takeLatest(actions.enableUsers.request, enableUsersAsync);
}

// lock
function* lockUsersAsync(action: { payload: IdType[] }) {
  try {
    yield call(UsersService.lock, { ids: action.payload });
    yield put(actions.lockUsers.success(action.payload));
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.lockUsers.failure(error));
  }
}

function* watchLockUsers() {
  yield takeLatest(actions.lockUsers.request, lockUsersAsync);
}

// filters
function* setUsersFilterAsync() {
  yield put(actions.fetchUsers.request());
}

function* watchUsersFilter() {
  yield takeLatest(actions.setUsersFilter, setUsersFilterAsync);
}

// fetch
function* fetchUsersListByGroupIdAsync(action: { payload: IdType }) {
  const filters: Filters = yield select((state: RootState) => state.usersList.filter);

  try {
    const users: UserListItem[] = yield call(UsersService.getListByGroupId, filters, action.payload);

    yield put(
      actions.fetchUsersByGroupId.success({ groupId: action.payload, data: { items: users, total: users?.length } }),
    );
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.fetchUsersByGroupId.failure(error));
  }
}

function* watchFetchUsersListByGroupId() {
  yield takeLatest(actions.fetchUsersByGroupId.request, fetchUsersListByGroupIdAsync);
}

// filters
function* setUsersByGroupIdFilterAsync(action: { payload: { filters: Filters; groupId: IdType } }) {
  yield put(actions.fetchUsersByGroupId.request(action.payload.groupId));
}

function* watchUsersByGroupIdFilter() {
  yield takeLatest(actions.setUsersByGroupIdFilter, setUsersByGroupIdFilterAsync);
}

// ungroup
function* ungroupUsersAsync(action: { payload: IdType[] }) {
  try {
    yield call(UsersService.ungroup, { ids: action.payload });
    yield put(actions.ungroupUsers.success(action.payload));
    Message.success(`${action.payload.length} User(s) has been ungrouped`);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.ungroupUsers.failure(error));
  }
}

function* watchUngroupUsers() {
  yield takeLatest(actions.ungroupUsers.request, ungroupUsersAsync);
}

// group
function* groupUsersAsync(action: { payload: { groupId: IdType; ids: IdType[] } }) {
  try {
    yield call(UsersService.group, { ids: action.payload.ids }, action.payload.groupId);
    yield put(actions.groupUsers.success(action.payload.ids));
    yield put(actions.fetchUsersByGroupId.request(action.payload.groupId));
    Message.success(`${action.payload.ids.length} User(s) has been grouped`);
  } catch (e) {
    const error = e as AxiosError | Error;
    yield put(actions.groupUsers.failure(error));
  }
}

function* watchGroupUsers() {
  yield takeLatest(actions.groupUsers.request, groupUsersAsync);
}

// all
function* watchUsersAll(): SagaGenerator {
  yield all([
    watchFetchUsersList(),
    watchFetchUserById(),
    watchCreateUser(),
    watchUpdateUser(),
    watchDeleteUsers(),
    watchEnableUsers(),
    watchLockUsers(),
    watchUsersFilter(),
    watchFetchUsersListByGroupId(),
    watchUsersByGroupIdFilter(),
    watchUngroupUsers(),
    watchGroupUsers(),
  ]);
}

export default watchUsersAll;
